import { codigoEquipamento } from '../utils/codigoEquipamento';
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '../services/api';

/* ============================================================
   PÁGINA PÚBLICA DO EQUIPAMENTO (P_IM_01)
   Aberta ao ler o QR code com o telemóvel (sem login).
   Mostra identificação, estado, o manual de instruções em português
   (e a declaração CE) quando estão marcados «visível em obra», o
   último relatório DL50 e as datas de manutenção/verificação.
   ============================================================ */

const fmtDate = (v) => (v ? new Date(v).toLocaleDateString('pt-PT') : '—');
// Ferramentas e RMM (registados também pelo telemóvel) distinguem-se no topo
const CATEGORIAS = {
    ferramenta: { icone: '🔧', label: 'Ferramenta' },
    rmm: { icone: '📏', label: 'RMM — equipamento de medição' },
};
const tamanho = (n) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const isOverdue = (v) => {
    if (!v) return false;
    const d = new Date(v); d.setHours(0, 0, 0, 0);
    const hoje = new Date(); hoje.setHours(0, 0, 0, 0);
    return d < hoje;
};

const EquipamentoPublico = () => {
    const { token } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [erro, setErro] = useState(null);

    useEffect(() => {
        let ativo = true;
        (async () => {
            try {
                const res = await api.get(`/equipamentos/public/${token}`);
                if (ativo) setData(res.data);
            } catch (e) {
                if (ativo) setErro(e.response?.status === 404 ? 'Equipamento não encontrado.' : 'Erro ao carregar o equipamento.');
            } finally {
                if (ativo) setLoading(false);
            }
        })();
        return () => { ativo = false; };
    }, [token]);

    const eq = data?.equipamento;
    const ultimo = data?.ultimo_relatorio;
    // O manual em português primeiro; depois folhetos de segurança e declarações CE
    const ordem = (d) => (d.categoria === 'manual' ? (/^manual/i.test(d.titulo || '') ? 0 : 1) : 2);
    const documentos = [...(data?.documentos || [])]
        .sort((a, b) => (ordem(a) - ordem(b)) || String(a.titulo).localeCompare(String(b.titulo), 'pt'));
    const cat = eq ? CATEGORIAS[eq.categoria] : null;
    // Um RMM verifica-se pelo IT.IM.01, não pelo DL 50/2005: sem relatório, a secção não diz nada
    const mostrarDl50 = !!ultimo || eq?.categoria !== 'rmm';

    // Estado global: baseia-se no último relatório se existir, senão no campo dl50_resultado.
    let apto = null;
    if (ultimo) apto = !!ultimo.resultado_usavel;
    else if (eq?.dl50_resultado) apto = /conforme/i.test(eq.dl50_resultado) && !/não|nao/i.test(eq.dl50_resultado);

    const relatorioUrl = ultimo ? `${api.defaults.baseURL}/dl50/relatorio/${ultimo.id}?t=${ultimo.share_token}` : null;

    const manut = eq ? [
        { l: 'Última revisão', v: eq.ultima_revisao_data || eq.data_revisao },
        { l: 'Próxima revisão', v: eq.proxima_revisao_data, prox: true },
        { l: 'Última calibração', v: eq.ultima_calibracao },
        { l: 'Próxima calibração', v: eq.proxima_calibracao, prox: true },
        { l: 'Última verificação DL50', v: eq.dl50_ultima_verificacao },
        { l: 'Próxima verificação DL50', v: eq.dl50_proxima_verificacao, prox: true },
    ].filter(x => x.v) : [];

    return (
        <div style={sty.page}>
            <div style={sty.card}>
                <div style={sty.brand}>SPROD</div>

                {loading ? (
                    <p style={{ textAlign: 'center', color: '#64748b' }}>A carregar…</p>
                ) : erro ? (
                    <div style={{ textAlign: 'center', color: '#b91c1c', padding: '20px 0' }}>{erro}</div>
                ) : (
                    <>
                        <div style={{ textAlign: 'center', marginBottom: 14 }}>
                            <div style={sty.icon}>{cat ? cat.icone : '🛠️'}</div>
                            {cat && <div style={sty.cat}>{cat.label}</div>}
                            <h1 style={sty.nome}>{eq.nome}</h1>
                            {(eq.marca || eq.modelo) && (
                                <div style={sty.sub}>{[eq.marca, eq.modelo].filter(Boolean).join(' · ')}</div>
                            )}
                            {apto !== null && (
                                <div style={{ ...sty.estado, ...(apto ? sty.estadoOk : sty.estadoNok) }}>
                                    {apto ? '✓ Apto para utilização' : '✕ Não apto'}
                                </div>
                            )}
                        </div>

                        <div style={sty.grid}>
                            <Info l="ID" v={codigoEquipamento(eq.id)} />
                            {eq.n_serie && <Info l="Nº de série" v={eq.n_serie} />}
                            {eq.matricula && <Info l="Matrícula" v={eq.matricula} />}
                            {eq.tipo && <Info l="Tipo" v={eq.tipo} />}
                            {eq.empresa_responsavel && <Info l="Empresa" v={eq.empresa_responsavel} />}
                        </div>

                        {/* Manual de instruções: é o que quem está a usar a ferramenta procura */}
                        {documentos.length > 0 && (
                            <div style={sty.section}>
                                <div style={sty.sectionTitle}>Manual e documentos</div>
                                {documentos.map((d, i) => (
                                    <a key={d.id}
                                        href={`${api.defaults.baseURL}/equipamentos/public/${token}/documentos/${d.id}`}
                                        target="_blank" rel="noopener noreferrer"
                                        style={i === 0 ? sty.btn : sty.btnSec}>
                                        {ordem(d) === 0 ? '📘' : ordem(d) === 1 ? '⚠️' : '📄'} {d.titulo}
                                        {d.tamanho ? <span style={sty.tam}> · {tamanho(d.tamanho)}</span> : null}
                                    </a>
                                ))}
                            </div>
                        )}

                        {/* Último relatório DL50 */}
                        {mostrarDl50 && (
                            <div style={sty.section}>
                                <div style={sty.sectionTitle}>Último relatório DL 50/2005</div>
                                {ultimo ? (
                                    <>
                                        <div style={sty.relRow}>
                                            <span>Relatório</span>
                                            <strong>{ultimo.relatorio_num || `#${ultimo.id}`}</strong>
                                        </div>
                                        <div style={sty.relRow}>
                                            <span>Data</span>
                                            <strong>{fmtDate(ultimo.data_verificacao)}</strong>
                                        </div>
                                        <div style={sty.relRow}>
                                            <span>Resultado</span>
                                            <strong style={{ color: ultimo.resultado_usavel ? '#16a34a' : '#dc2626' }}>
                                                {ultimo.resultado_usavel ? 'Apto' : 'Não apto'}
                                            </strong>
                                        </div>
                                        <a href={relatorioUrl} target="_blank" rel="noopener noreferrer" style={sty.btn}>
                                            📄 Ver relatório (PDF)
                                        </a>
                                    </>
                                ) : (
                                    <div style={{ color: '#94a3b8', fontSize: '0.9rem' }}>Sem relatórios DL50 registados.</div>
                                )}
                            </div>
                        )}

                        {/* Manutenção / verificações */}
                        {manut.length > 0 && (
                            <div style={sty.section}>
                                <div style={sty.sectionTitle}>Manutenção e verificações</div>
                                {manut.map((m, i) => (
                                    <div key={i} style={sty.relRow}>
                                        <span>{m.l}</span>
                                        <strong style={{ color: m.prox && isOverdue(m.v) ? '#dc2626' : '#0f172a' }}>
                                            {fmtDate(m.v)}{m.prox && isOverdue(m.v) ? ' ⚠' : ''}
                                        </strong>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>
            <div style={sty.footer}>SPROD · Sistema de Gestão</div>
        </div>
    );
};

const Info = ({ l, v }) => (
    <div style={sty.info}>
        <div style={sty.infoL}>{l}</div>
        <div style={sty.infoV}>{v}</div>
    </div>
);

const sty = {
    page: { minHeight: '100vh', background: '#f1f5f9', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '20px 14px', fontFamily: 'system-ui, sans-serif' },
    card: { background: '#fff', borderRadius: 16, boxShadow: '0 10px 40px rgba(0,0,0,0.08)', padding: 22, width: '100%', maxWidth: 460 },
    brand: { fontWeight: 800, letterSpacing: '0.15em', color: '#1e40af', textAlign: 'center', fontSize: '0.85rem', marginBottom: 12 },
    icon: { fontSize: 40, lineHeight: 1 },
    cat: { marginTop: 6, fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' },
    nome: { margin: '8px 0 2px', fontSize: '1.4rem', color: '#0f172a' },
    sub: { color: '#64748b', fontSize: '0.95rem' },
    estado: { display: 'inline-block', marginTop: 10, padding: '6px 16px', borderRadius: 20, fontWeight: 700, fontSize: '0.9rem' },
    estadoOk: { background: '#dcfce7', color: '#166534' },
    estadoNok: { background: '#fee2e2', color: '#991b1b' },
    grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, margin: '10px 0' },
    info: { background: '#f8fafc', borderRadius: 8, padding: '8px 10px' },
    infoL: { fontSize: '0.68rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em' },
    infoV: { fontSize: '0.92rem', color: '#0f172a', fontWeight: 600, wordBreak: 'break-word' },
    section: { border: '1px solid #e2e8f0', borderRadius: 12, padding: 14, marginTop: 12 },
    sectionTitle: { fontWeight: 700, fontSize: '0.9rem', color: '#334155', marginBottom: 8 },
    relRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', fontSize: '0.9rem', color: '#475569', borderBottom: '1px solid #f1f5f9' },
    btn: { display: 'block', textAlign: 'center', marginTop: 12, background: '#2563eb', color: '#fff', padding: '11px', borderRadius: 10, textDecoration: 'none', fontWeight: 600 },
    btnSec: { display: 'block', textAlign: 'center', marginTop: 10, background: '#fff', color: '#1d4ed8', border: '1px solid #bfdbfe', padding: '10px', borderRadius: 10, textDecoration: 'none', fontWeight: 600 },
    tam: { fontWeight: 400, opacity: 0.8, fontSize: '0.82rem' },
    footer: { color: '#94a3b8', fontSize: '0.75rem', marginTop: 16 },
};

export default EquipamentoPublico;
